import { Lock } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { type DeploymentFacts, environmentBadge } from "@/lib/environment";
import { cn } from "@/lib/utils";

interface EnvironmentBadgeProps {
  /** Where the dashboard runs (`GET /mcp/overview`); undefined while the overview loads. */
  overview: DeploymentFacts | undefined;
  /**
   * The compact form, for the sidebar's rail only, where the full one does
   * not fit: the name with a lock when read-only. The words stay for screen
   * readers and in the tooltip, which a mouse or the keyboard opens; a touch
   * screen never does, so everywhere else the badge takes its full form.
   */
  compact?: boolean;
  /** Where the tooltip opens: right of the rail, under the badge elsewhere. */
  tooltipSide?: "right" | "bottom";
  className?: string;
}

/**
 * The environment's name in a small blue outline, so a test environment is
 * never taken for production, followed by "Alleen lezen" when it never writes
 * to Odoo or any other system. Production shows nothing at all, and neither
 * does a loading or failed overview. Like the connector's status, it takes
 * keyboard focus, so its tooltip can be read without a mouse; it is as tall in
 * both forms, so collapsing the sidebar moves nothing below it.
 */
export function EnvironmentBadge({
  overview,
  compact = false,
  tooltipSide = "bottom",
  className,
}: EnvironmentBadgeProps) {
  // `environmentBadge` translates through i18next; this re-renders it on a switch.
  const { t } = useTranslation();
  const badge = environmentBadge(overview);
  if (!badge) {
    return null;
  }
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <p
          tabIndex={0}
          data-environment={badge.name.toLowerCase()}
          className={cn(
            "text-sidebar-muted focus-visible:ring-ring inline-flex h-5 max-w-full min-w-0 items-center gap-2 rounded-sm text-xs font-semibold focus-visible:ring-2 focus-visible:outline-none",
            className,
          )}
        >
          <span className="border-primary text-primary inline-flex h-5 min-w-0 items-center gap-1 rounded-sm border px-1.5">
            {compact && badge.readOnly && <Lock className="size-3 shrink-0" aria-hidden="true" />}
            <span className="truncate">{badge.name}</span>
          </span>
          {badge.readOnly &&
            (compact ? (
              <span className="sr-only">{t("environment.readOnly")}</span>
            ) : (
              <span className="inline-flex min-w-0 items-center gap-1">
                <Lock className="size-3 shrink-0" aria-hidden="true" />
                <span className="truncate">{t("environment.readOnly")}</span>
              </span>
            ))}
        </p>
      </TooltipTrigger>
      <TooltipContent
        side={tooltipSide}
        align={tooltipSide === "right" ? "center" : "start"}
        sideOffset={8}
      >
        {badge.title}
      </TooltipContent>
    </Tooltip>
  );
}
